import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  keyframes,
} from '@mui/material';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import UpgradePrompt from '@/components/UpgradePrompt/UpgradePrompt';
import { useCountdown } from '@/hooks/useCountdown';
import { useNavigationBlock } from '@/hooks/useNavigationBlock';
import { useVideoUpload } from '@/pages/Record/Recorder/hooks';
import { RecordingControls, UploadSection } from '@/pages/Record/Recorder/components';
import { useAudioRecorder } from './hooks/useAudioRecorder';
import VoiceWaveform from './components/VoiceWaveform';

const countdownPulse = keyframes`
  0% { transform: scale(1.4); opacity: 0.7; }
  60% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.95); opacity: 1; }
`;

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const AudioRecordingPage: React.FC = () => {
  const planFeatures = useCurrentPlanFeatures();
  const planLimitSeconds = useMemo(() => planFeatures.video_seconds ?? 120, [planFeatures.video_seconds]);
  const planLimitFormatted = useMemo(() => formatTime(planLimitSeconds), [planLimitSeconds]);

  const hasAudioRecordingAccess = planFeatures.features.audioRecording;

  const [featureAvailable, setFeatureAvailable] = useState(true);
  const [recordedFile, setRecordedFile] = useState<File | null>(null);
  const [recordingDuration, setRecordingDuration] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const autoUploadTriggeredRef = useRef(false);
  const restartRequestedRef = useRef(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const isRestartIntentRef = useRef(false);

  // Microphone enumeration
  const [availableMics, setAvailableMics] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('notego_preferred_mic') ?? null;
    } catch {
      return null;
    }
  });

  // Stable ref for startPreview to avoid effect dependency churn
  const startPreviewRef = useRef<(deviceIdOverride?: string) => Promise<boolean>>(() => Promise.resolve(false));

  useEffect(() => {
    if (typeof window === 'undefined' || !hasAudioRecordingAccess) return;
    const hasMediaDevices = !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function');
    const hasMediaRecorder = typeof MediaRecorder !== 'undefined';
    setFeatureAvailable(hasMediaDevices && hasMediaRecorder);

    if (!hasMediaDevices) return;

    const initMicAndPreview = async () => {
      // Start preview — acquires mic and creates analyser for live waveform
      const previewOk = await startPreviewRef.current();

      if (!previewOk) {
        // Permission denied or no mic — try bare enumeration
        try {
          const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          tempStream.getTracks().forEach((t) => t.stop());
        } catch {
          return;
        }
      }

      // Enumerate devices (labels populated because a stream was acquired)
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter((d) => d.kind === 'audioinput' && d.deviceId);
      setAvailableMics(mics);
    };
    void initMicAndPreview();
  }, [hasAudioRecordingAccess]);

  const uploader = useVideoUpload({
    onUploadComplete: useCallback((result: { success: boolean }) => {
      if (result.success) setUploadComplete(true);
    }, []),
    navigateState: { isAudioRecording: true },
  });

  const handleRecordingComplete = useCallback(
    (file: File, durationSeconds: number) => {
      setError(null);
      setRecordingDuration(durationSeconds);
      setRecordedFile(file);

      if (durationSeconds > planLimitSeconds) {
        setError(
          `Your recording is ${formatTime(Math.round(durationSeconds))}, which exceeds your plan limit of ${planLimitFormatted}. Please record a shorter session.`,
        );
      }
    },
    [planLimitFormatted, planLimitSeconds],
  );

  const { countdownValue, isCountingDown, runCountdown, cancelCountdown } = useCountdown();

  const handleStaleMic = useCallback(() => {
    setSelectedMicId(null);
  }, []);

  const audioRecorder = useAudioRecorder({
    maxDuration: planLimitSeconds,
    audioDeviceId: selectedMicId ?? undefined,
    onRecordingComplete: handleRecordingComplete,
    onStaleMic: handleStaleMic,
  });

  // Keep ref in sync for the mount effect
  startPreviewRef.current = audioRecorder.startPreview;

  const handleStart = useCallback(async () => {
    if (isCountingDown || audioRecorder.isRecording) return;

    // Ensure mic access before countdown — preview usually handles this on mount,
    // but if preview failed or was never started, acquire now
    if (!audioRecorder.isPreviewing) {
      const previewOk = await audioRecorder.startPreview();
      if (!previewOk) {
        // CONTRACT #7: never call startRecording() here. This is a live restart
        // path (the restart effect below routes through handleStart), and a
        // transient preview failure — e.g. resetRecording just released the mic
        // and re-acquisition throws NotReadableError — would start a recording
        // with no countdown. startPreview() surfaces the mic error itself.
        return;
      }
    }

    const completed = await runCountdown();
    if (!completed) return;
    void audioRecorder.startRecording();
  }, [
    isCountingDown,
    audioRecorder.isRecording,
    audioRecorder.isPreviewing,
    audioRecorder.startPreview,
    audioRecorder.startRecording,
    runCountdown,
  ]);

  const handleMicSelect = useCallback(
    (deviceId: string) => {
      if (isCountingDown || audioRecorder.isRecording || audioRecorder.isPaused) return;
      setSelectedMicId(deviceId);
      try {
        localStorage.setItem('notego_preferred_mic', deviceId);
      } catch {
        /* quota */
      }
      // Restart preview on new device for immediate waveform feedback
      audioRecorder.stopPreview();
      void audioRecorder.startPreview(deviceId);
    },
    [
      isCountingDown,
      audioRecorder.isRecording,
      audioRecorder.isPaused,
      audioRecorder.stopPreview,
      audioRecorder.startPreview,
    ],
  );

  // Auto-upload when recording completes (matches screen recording behavior)
  useEffect(() => {
    if (
      recordedFile &&
      recordingDuration !== null &&
      !autoUploadTriggeredRef.current &&
      !uploader.isUploading &&
      !uploadComplete
    ) {
      if (recordingDuration <= planLimitSeconds) {
        autoUploadTriggeredRef.current = true;
        void uploader.uploadVideo(recordedFile);
      }
    }
    if (!recordedFile) {
      autoUploadTriggeredRef.current = false;
      setUploadComplete(false);
    }
  }, [recordedFile, recordingDuration, planLimitSeconds, uploader.isUploading, uploader.uploadVideo, uploadComplete]);

  const handleUpload = useCallback(async () => {
    if (!recordedFile || recordingDuration === null || recordingDuration > planLimitSeconds) return;
    await uploader.uploadVideo(recordedFile);
  }, [recordedFile, recordingDuration, planLimitSeconds, uploader.uploadVideo]);

  const handleReset = useCallback(() => {
    cancelCountdown();
    setRecordedFile(null);
    setRecordingDuration(null);
    setError(null);
    setUploadComplete(false);
    autoUploadTriggeredRef.current = false;
    uploader.clearUploadError();
    audioRecorder.resetRecording();
    // Only restart preview for discard flow — restart flow handles preview via handleStart
    if (!restartRequestedRef.current) {
      void audioRecorder.startPreview(selectedMicId ?? undefined);
    }
  }, [
    cancelCountdown,
    uploader.clearUploadError,
    audioRecorder.resetRecording,
    audioRecorder.startPreview,
    selectedMicId,
  ]);

  const handleRestart = useCallback(() => {
    restartRequestedRef.current = true;
    handleReset();
  }, [handleReset]);

  // Confirmation dialog handlers for reset/discard
  const handleResetRequest = useCallback(() => {
    if (audioRecorder.isRecording || audioRecorder.isPaused || recordedFile) {
      isRestartIntentRef.current = false;
      setConfirmResetOpen(true);
    } else {
      handleReset();
    }
  }, [audioRecorder.isRecording, audioRecorder.isPaused, recordedFile, handleReset]);

  const handleRestartRequest = useCallback(() => {
    if (audioRecorder.isRecording || audioRecorder.isPaused) {
      isRestartIntentRef.current = true;
      setConfirmResetOpen(true);
    } else {
      handleRestart();
    }
  }, [audioRecorder.isRecording, audioRecorder.isPaused, handleRestart]);

  const handleConfirmReset = useCallback(() => {
    setConfirmResetOpen(false);
    if (isRestartIntentRef.current) {
      handleRestart();
    } else {
      handleReset();
    }
  }, [handleReset, handleRestart]);

  const handleCancelReset = useCallback(() => {
    setConfirmResetOpen(false);
  }, []);

  // After reset completes and isRecording flips to false, trigger the start
  useEffect(() => {
    if (restartRequestedRef.current && !audioRecorder.isRecording && !isCountingDown) {
      restartRequestedRef.current = false;
      void handleStart();
    }
  }, [audioRecorder.isRecording, isCountingDown, handleStart]);

  const isActive = audioRecorder.isRecording || audioRecorder.isPaused || isCountingDown;

  useNavigationBlock(
    isActive,
    'You have an active audio recording. Leaving this page will stop the recording and your progress will be lost. Continue?',
  );

  const hasRecording = !!recordedFile;

  return (
    <Box
      px={{ xs: '16px', sm: '32px' }}
      py="24px"
      sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '960px', mx: 'auto', gap: 3 }}
    >
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Record Audio
        </Typography>
      </Box>

      {!planFeatures.isLoading && !hasAudioRecordingAccess && (
        <UpgradePrompt
          featureName="Audio Recording"
          description="Audio recording lets you capture microphone audio and send it through the NoteGo processing pipeline for transcription and notes."
        />
      )}

      {hasAudioRecordingAccess && !featureAvailable && (
        <Alert severity="warning">
          Audio recording is not available in this browser. Please ensure you are using a supported browser with HTTPS
          or localhost.
        </Alert>
      )}

      {hasAudioRecordingAccess && !hasRecording && (
        <Card>
          <CardContent>
            {audioRecorder.error && (
              <Alert severity="error" onClose={audioRecorder.clearError} sx={{ mb: 3 }}>
                {audioRecorder.error}
              </Alert>
            )}

            {/* Voice-reactive waveform */}
            <Box sx={{ mb: 1, position: 'relative' }}>
              <VoiceWaveform
                analyserRef={audioRecorder.analyserRef}
                active={isActive}
                paused={audioRecorder.isPaused}
                previewing={audioRecorder.isPreviewing && !isActive}
              />
              {countdownValue && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: 700,
                      color: countdownValue === 'Go!' ? '#4ade80' : 'primary.main',
                      textShadow:
                        countdownValue === 'Go!'
                          ? '0 0 20px rgba(74,222,128,0.4), 0 0 40px rgba(74,222,128,0.2), 0 2px 8px rgba(0,0,0,0.15)'
                          : '0 0 20px rgba(43,159,255,0.5), 0 0 40px rgba(43,159,255,0.3), 0 2px 8px rgba(0,0,0,0.15)',
                      fontSize: {
                        xs: countdownValue === 'Go!' ? '70px' : '80px',
                        sm: countdownValue === 'Go!' ? '100px' : '120px',
                        md: countdownValue === 'Go!' ? '140px' : '160px',
                      },
                      animation: `${countdownPulse} 0.5s ease-out`,
                    }}
                  >
                    {countdownValue}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Recording toolbar (matches video recorder) */}
            <RecordingControls
              isRecording={audioRecorder.isRecording}
              isPaused={audioRecorder.isPaused}
              elapsed={audioRecorder.elapsed}
              maxDuration={planLimitSeconds}
              disabled={isCountingDown}
              onStart={() => {
                void handleStart();
              }}
              onPause={audioRecorder.pauseRecording}
              onResume={audioRecorder.resumeRecording}
              onStop={audioRecorder.stopRecording}
              onReset={handleResetRequest}
              onRestart={handleRestartRequest}
              microphones={availableMics}
              selectedMicId={selectedMicId}
              onMicSelect={handleMicSelect}
              isMicActive={!!selectedMicId || availableMics.length > 0}
            />

            <Alert severity="info" sx={{ mt: 3 }}>
              Do not close or navigate away from this page while recording.
            </Alert>
          </CardContent>
        </Card>
      )}

      {hasAudioRecordingAccess && hasRecording && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              {uploadComplete ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 6, sm: 8 },
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    Upload Complete!
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Redirecting to your note...
                  </Typography>
                </Box>
              ) : (
                <>
                  {error && !uploader.isUploading && (
                    <Alert severity="error" onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  <UploadSection
                    isUploading={uploader.isUploading}
                    progress={uploader.uploadProgress}
                    error={uploader.uploadError}
                    selectedDuration={recordingDuration ?? 0}
                    maxDuration={planLimitSeconds}
                    onUpload={handleUpload}
                    onReset={handleResetRequest}
                    onClearError={uploader.clearUploadError}
                    formatTime={formatTime}
                    mediaType="audio"
                    recoveryFile={recordedFile ? { file: recordedFile, name: recordedFile.name } : null}
                  />
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Reset confirmation dialog */}
      <Dialog open={confirmResetOpen} onClose={handleCancelReset}>
        <DialogTitle>Discard Recording?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {audioRecorder.isRecording
              ? 'You have an active recording in progress. Are you sure you want to discard it and start over?'
              : 'Are you sure you want to discard this recording and start over?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReset}>Cancel</Button>
          <Button onClick={handleConfirmReset} color="error">
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AudioRecordingPage;
